<template>
    <div class="journey-segment">
        <div class="journey-segment-indicator">
            <div class="indicator" />
            <div class="indicator" />
        </div>
        <div class="journey-segment-details">
            <Location :data="item.departure" />
            <Info
                :label="label"
                :name="item.company.name"
                :ferryName="item.company?.ferryName"
                :logo="item.company?.logo"
                :time="item.time"
                :ticketType="item?.class"
                :technicalStop="item.technicalStop">
                <div v-if="stops > 0" class="indicator stop" />
            </Info>
            <Location :data="item.arrival" />
        </div>
    </div>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { defineComponent, computed, PropType } from 'vue';
    import { JourneyCompany, JourneySegment } from '../Journey.interfaces';
    import { getStops } from '../Journey.helpers';
    import Location from '../Shared/Location/Location.vue';
    import Info from '../Shared/Info/Info.vue';

    export default defineComponent({
        name: 'Minified',
        components: { Location, Info },
        props: {
            type: String,
            time: {
                type: String as PropType<string | null>,
            },
            company: {
                type: Object as PropType<JourneyCompany>,
                required: true,
            },
            segments: {
                type: Array as PropType<JourneySegment[]>,
                required: true,
            },
        },
        setup(props) {
            const item = computed((): JourneySegment => {
                const firstSegment = props.segments[0];
                const lastSegment = props.segments[props.segments.length - 1];
                const hasTechnicalStop = props.segments.some((s) => s.technicalStop);

                return {
                    departure: firstSegment.departure,
                    arrival: lastSegment.arrival,
                    company: props.company,
                    class: firstSegment.class,
                    time: props.time,
                    technicalStop: hasTechnicalStop,
                };
            });

            const stops = computed(() => getStops(props.segments));

            const label = computed((): string | null => {
                switch (props.type) {
                    case 'Flight':
                        if (stops.value <= 0) {
                            return t({ key: 'shared.direct_flight' });
                        } else if (stops.value === 1) {
                            return t({
                                key: 'shared.stop',
                                prefix: `${stops.value} `,
                            });
                        } else {
                            return t({
                                key: 'shared.stops',
                                prefix: `${stops.value} `,
                            });
                        }
                    case 'Ferry':
                        if (stops.value === 1) {
                            return t({
                                key: 'shared.stop',
                                prefix: `${stops.value} `,
                            });
                        } else if (stops.value > 1) {
                            return t({
                                key: 'shared.stops',
                                prefix: `${stops.value} `,
                            });
                        } else {
                            return null;
                        }
                    default:
                        return null;
                }
            });

            return {
                item,
                stops,
                label,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward '../Journey.scss';
</style>
